import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ListResponse } from '../../../../../models/list-response';
import { AccountingConnection, getAccountingSoftwareDisplay } from '../../../../../models/organization-config';
import { AccountService } from '../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { environment } from '../../../../../../environments/environment';
import { SimpleMessageModalComponent } from '../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { AssignAccountingConnectionModalComponent } from './modals/assign-accounting-connection-modal/assign-accounting-connection-modal.component';
import { CallbackResult, CallbackResultStatus } from '../../../../../utils';
import { LocationService } from '../../../../../services/location/location.service';
import { AccountingSoftwareType } from '../../../../../models/organization-config-enums';


@Component({
  selector: 'app-accounting-connections',
  templateUrl: './accounting-connections.component.html',
  styleUrls: ['./accounting-connections.component.css']
})
export class AccountingConnectionsComponent implements OnInit {
  public conns: ListResponse<AccountingConnection>;

  public loadingConns = false;

  public successMessage = '';
  public errorMessage = '';
  public modalResult: CallbackResult = null;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public getAccountingSoftwareDisplay = getAccountingSoftwareDisplay;
  public CallbackResultStatus = CallbackResultStatus;

  constructor(
    public accountService: AccountService,
    private orgConfigService: OrganizationConfigService,
    private locationService: LocationService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      return;
    }

    // Make browser history available to the template.
    this.history = history;

    this.loadConns();
  }

  public resultsPresent() {
    return this.conns && this.conns.results && (this.conns.results.length > 0);
  }

  public handleAssignConn(conn: AccountingConnection) {
    this.successMessage = '';
    this.errorMessage = '';
    this.modalResult = null;

    const modalRef = this.modalService.open(AssignAccountingConnectionModalComponent);
    modalRef.componentInstance.conn = conn;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadConns();
      this.locationService.loadLocations();
    };
  }

  public handleDuplicateConn(conn: AccountingConnection) {
    this.successMessage = '';
    this.errorMessage = '';
    this.modalResult = null;

    let messageText = 'Are you sure you want to duplicate the "' + conn.name + '" accounting connection?';
    if (conn.accounting_software === AccountingSoftwareType.xero) {
      messageText += ' This will create a second connection for the same Xero account, which allows you to configure sites to use different revenue accounts while sharing the same Xero account.'
    }
    else if (conn.accounting_software === AccountingSoftwareType.quickbooksOnline) {
      messageText += ' This will create a second connection for the same Quickbooks Online account, which allows you to configure sites to use different revenue accounts while sharing the same Quickbooks account.'
    }

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Duplicate Accounting Connection';
    modalRef.componentInstance.messageText = messageText;
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingConns = true;

      this.orgConfigService
        .duplicateAccountingConnection(conn)
        .then(() => {
          this.successMessage = 'Connection "' + conn.name + '" duplicated successfully.';
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to duplicate this connection.'
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadConns();
        });
    };
  }

  public handleRemoveConn(conn: AccountingConnection) {
    this.successMessage = '';
    this.errorMessage = '';
    this.modalResult = null;

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Accounting Connection';
    modalRef.componentInstance.messageText = 'Are you sure you want to remove the "' + conn.name + '" accounting connection?';
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingConns = true;

      this.orgConfigService
        .removeAccountingConnection(conn.url)
        .then(() => {
          this.successMessage = 'Connection "' + conn.name + '" removed successfully.';
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to remove this connection.'
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadConns();
          this.locationService.loadLocations();
        });
    };
  }

  public getQuickbooksOAuthInitURL() {
    return environment.api_url + 'quickbooks/oauth-init/?token=' + this.accountService.getToken();
  }

  public getXeroOAuthInitURL() {
    return environment.api_url + 'xero/oauth-init/?token=' + this.accountService.getToken();
  }

  public handleAddNewManualConnection() {
    this.successMessage = '';
    this.errorMessage = '';
    this.modalResult = null;

    this.loadingConns = true;

    let nameNum = 1;
    let name = '';
    while (true) {
      name = 'Manual Connection ' + nameNum.toString();
      let isDuplicate = false;
      for (const conn of this.conns.results) {
        if (conn.name.trim() === name) {
          isDuplicate = true;
        }
      }
      if (!isDuplicate) { break; }
      nameNum += 1;
    }

    this.orgConfigService
      .addAccountingConnection(name)
      .then(conn => {
        this.router.navigate(['/accounting-connections/configure', conn.id.toString()]);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to create a new manual connection.'
        } else {
          this.serverError = reason.status;
        }
        this.loadingConns = false;
      });
  }

  private loadConns() {
    this.loadingConns = true;

    this.orgConfigService
      .getAccountingConnections(1, 1000)
      .then(response => {
        this.conns = response;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingConns = false; });
  }
}
